import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import { motion } from 'framer-motion';

const SCHEMA_CONTATO = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${import.meta.env.VITE_SITE_URL}/contato#webpage`,
  url: `${import.meta.env.VITE_SITE_URL}/contato`,
  name: 'Contato Engeporto Engenharia Sorocaba | Orçamento AVCB, PGR e Perícias',
  description: 'Entre em contato com a Engeporto Engenharia para solicitar orçamento de AVCB, CLCB, PGR, LTCAT e perícias técnicas em Sorocaba e Campinas.',
  inLanguage: 'pt-BR',
  isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
  mainEntity: {
    '@type': 'LocalBusiness',
    name: 'Engeporto Engenharia e Serviços Ltda.',
    telephone: '+55-15-99671-9308',
    email: 'engeporto.contato@gmail.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Rua Floriano Pires, 103',
      addressLocality: 'Porto Feliz',
      addressRegion: 'SP',
      postalCode: '18545-060',
      addressCountry: 'BR',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'],
        opens: '08:00',
        closes: '12:00',
      },
    ],
  },
});

const ContatoPage = () => {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = 'Contato Engeporto Sorocaba Campinas | Orçamento AVCB, PGR e Perícias';
    const setMeta = (name: string, content: string, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      const el = document.querySelector(sel) as HTMLMetaElement | null;
      if (el) el.setAttribute('content', content);
    };
    setMeta('description', 'Entre em contato com a Engeporto Engenharia para solicitar orçamento de AVCB, CLCB, PGR, LTCAT e perícias técnicas nas Regiões Metropolitanas de Sorocaba e Campinas. Atendimento rápido.');
    setMeta('keywords', 'contato Engeporto, orçamento AVCB Sorocaba, orçamento PGR Campinas, telefone engenharia segurança');
    setMeta('og:title', 'Contato Engeporto | Orçamento AVCB, PGR e Perícias — Sorocaba Campinas', true);
    setMeta('og:description', 'Solicite orçamento de AVCB, CLCB, PGR, LTCAT e perícias técnicas em Sorocaba e Campinas.', true);
    setMeta('og:url', `${import.meta.env.VITE_SITE_URL}/contato`, true);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/contato`);
    return () => { canonical?.remove(); };
  }, []);

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    servico: '',
    mensagem: '',
  });

  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const formatTelefone = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits.length ? `(${digits}` : '';
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'telefone') {
      setFormData({ ...formData, telefone: formatTelefone(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    if (name === 'email') {
      if (value && !isValidEmail(value)) {
        setEmailError('Por favor, insira um e-mail válido (ex: nome@dominio.com)');
      } else {
        setEmailError('');
      }
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    if (!formData.email) {
      setEmailError('O e-mail é obrigatório');
    } else if (!isValidEmail(formData.email)) {
      setEmailError('Por favor, insira um e-mail válido (ex: nome@dominio.com)');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setEmailTouched(true);
    if (!formData.email || !isValidEmail(formData.email)) {
      setEmailError(!formData.email ? 'O e-mail é obrigatório' : 'Por favor, insira um e-mail válido (ex: nome@dominio.com)');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6igobpbfjp31emak6qg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        navigate('/obrigado');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: SCHEMA_CONTATO }}
      />
      <Navbar />
      
      <section className="pt-36 lg:pt-28 pb-16 bg-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center pt-8 md:pt-12"
          >
            <h1 className="font-display font-bold text-3xl md:text-5xl lg:text-6xl text-white mb-4 md:mb-6">
              Entre em Contato
            </h1>
            <p className="text-base md:text-xl text-neutral-300 max-w-3xl mx-auto">
              Estamos prontos para atender sua empresa nas Regiões Metropolitanas de Sorocaba e Campinas
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-semibold mb-4 md:mb-6">
                Solicite um Orçamento
              </div>
              <h2 className="font-display font-bold text-2xl md:text-4xl text-[#0A1628] mb-4 md:mb-6">
                Fale com Nossos Especialistas
              </h2>
              <p className="text-base md:text-lg text-neutral-700 mb-5 md:mb-6 leading-relaxed">
                Preencha o formulário abaixo e nossa equipe entrará em contato em até 24 horas para entender suas necessidades e apresentar a melhor solução técnica.
              </p>

              <div className="flex items-center gap-4 mb-8">
                <a
                  href="https://wa.me/5515996719308?text=Ol%C3%A1!%20Vim%20pela%20p%C3%A1gina%20de%20Contato%20e%20gostaria%20de%20solicitar%20um%20or%C3%A7amento."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A1628] px-6 py-3 rounded-full font-bold hover:bg-[#C4A137] transition-all shadow-md whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  SOLICITAR ORÇAMENTO
                </a>
                <span className="text-sm text-neutral-500">ou preencha o formulário abaixo</span>
              </div>

              <form id="contato-form" data-readdy-form onSubmit={handleSubmit} className="bg-neutral-50 rounded-3xl p-8 shadow-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-semibold text-[#0A1628] mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-[#0A1628] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                      placeholder="Seu nome"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-[#0A1628] mb-2">
                      E-mail *
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleEmailBlur}
                        required
                        className={`w-full px-4 py-3 bg-white border rounded-xl text-[#0A1628] focus:outline-none focus:ring-2 transition-all pr-10 ${
                          emailTouched && emailError
                            ? 'border-red-400 focus:ring-red-300'
                            : emailTouched && formData.email && !emailError
                            ? 'border-green-400 focus:ring-green-300'
                            : 'border-neutral-200 focus:ring-[#D4AF37] focus:border-transparent'
                        }`}
                        placeholder="seu@email.com"
                      />
                      {emailTouched && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                          {emailError ? (
                            <i className="ri-error-warning-line text-red-500 text-lg"></i>
                          ) : formData.email ? (
                            <i className="ri-checkbox-circle-line text-green-500 text-lg"></i>
                          ) : null}
                        </div>
                      )}
                    </div>
                    {emailTouched && emailError && (
                      <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                        <i className="ri-information-line"></i>
                        {emailError}
                      </p>
                    )}
                    {emailTouched && !emailError && formData.email && (
                      <p className="mt-1.5 text-xs text-green-600 flex items-center gap-1">
                        <i className="ri-checkbox-circle-line"></i>
                        E-mail válido
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="telefone" className="block text-sm font-semibold text-[#0A1628] mb-2">
                      Telefone *
                    </label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      required
                      inputMode="numeric"
                      className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-[#0A1628] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                      placeholder="(15) 99999-9999"
                    />
                  </div>

                  <div>
                    <label htmlFor="empresa" className="block text-sm font-semibold text-[#0A1628] mb-2">
                      Empresa
                    </label>
                    <input
                      type="text"
                      id="empresa"
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-[#0A1628] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                      placeholder="Nome da empresa"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="servico" className="block text-sm font-semibold text-[#0A1628] mb-2">
                    Serviço de Interesse *
                  </label>
                  <select
                    id="servico"
                    name="servico"
                    value={formData.servico}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-[#0A1628] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="">Selecione um serviço</option>
                    <option value="Prevenção de Incêndio">Prevenção de Incêndio</option>
                    <option value="Segurança do Trabalho">Segurança do Trabalho</option>
                    <option value="Avaliações e Perícias">Avaliações e Perícias</option>
                    <option value="Treinamentos">Treinamentos</option>
                    <option value="Consultoria">Consultoria</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="mensagem" className="block text-sm font-semibold text-[#0A1628] mb-2">
                    Mensagem *
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-[#0A1628] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all resize-none"
                    placeholder="Descreva suas necessidades..."
                  ></textarea>
                  <p className="text-xs text-neutral-500 mt-2">
                    Máximo de 500 caracteres ({formData.mensagem.length}/500)
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-full text-lg font-bold hover:bg-[#C4A137] transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
                </button>

                {submitStatus === 'success' && (
                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-center">
                    <i className="ri-checkbox-circle-line text-2xl mb-2"></i>
                    <p className="font-semibold">Mensagem enviada com sucesso!</p>
                    <p className="text-sm">Entraremos em contato em breve.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 text-center">
                    <i className="ri-error-warning-line text-2xl mb-2"></i>
                    <p className="font-semibold">Erro ao enviar mensagem.</p>
                    <p className="text-sm">Tente novamente ou entre em contato por telefone.</p>
                  </div>
                )}
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-[#0A1628] mb-6 md:mb-8">
                  Informações de Contato
                </h3>

                <div className="space-y-5 md:space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#D4AF37]/10 rounded-2xl flex-shrink-0">
                      <i className="ri-phone-line text-xl md:text-2xl text-[#D4AF37]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-base md:text-lg text-[#0A1628] mb-1">Telefone e WhatsApp</h4>
                      <a href="tel:+5515996719308" className="text-sm md:text-base text-neutral-700 hover:text-[#D4AF37] transition-colors cursor-pointer">
                        (15) 99671-9308
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#D4AF37]/10 rounded-2xl flex-shrink-0">
                      <i className="ri-mail-line text-xl md:text-2xl text-[#D4AF37]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-base md:text-lg text-[#0A1628] mb-1">E-mail</h4>
                      <a href="mailto:engeporto.contato@gmail.com" className="text-sm md:text-base text-neutral-700 hover:text-[#D4AF37] transition-colors cursor-pointer">
                        engeporto.contato@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#D4AF37]/10 rounded-2xl flex-shrink-0">
                      <i className="ri-building-line text-xl md:text-2xl text-[#D4AF37]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-base md:text-lg text-[#0A1628] mb-1">Endereço</h4>
                      <p className="text-sm md:text-base text-neutral-700 leading-relaxed">
                        Rua Floriano Pires, 103 - Porto Feliz - SP, Brasil<br />
                        <span className="text-xs md:text-sm text-neutral-500">CEP: 18545-060</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#D4AF37]/10 rounded-2xl flex-shrink-0">
                      <i className="ri-map-pin-line text-xl md:text-2xl text-[#D4AF37]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-base md:text-lg text-[#0A1628] mb-1">Regiões de Atendimento</h4>
                      <p className="text-sm md:text-base text-neutral-700">
                        Região Metropolitana de Sorocaba<br />
                        Região Metropolitana de Campinas
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-50 rounded-3xl p-6 md:p-8">
                <h4 className="font-display font-bold text-xl md:text-2xl text-[#0A1628] mb-4">
                  Horário de Atendimento
                </h4>
                <div className="space-y-3 text-sm md:text-base text-neutral-700">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Segunda a Sexta:</span>
                    <span>08:00 - 18:00</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Sábado:</span>
                    <span>08:00 - 12:00</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Domingo:</span>
                    <span>Fechado</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#0A1628] rounded-3xl p-6 md:p-8 text-white">
                <h4 className="font-display font-bold text-xl md:text-2xl mb-4">
                  Atendimento Emergencial
                </h4>
                <p className="text-sm md:text-base text-neutral-300 mb-5 md:mb-6">
                  Para situações urgentes, entre em contato pelo WhatsApp. Respondemos rapidamente para atender suas necessidades.
                </p>
                <a
                  href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Vim pela página de Contato e preciso de atendimento emergencial.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A1628] px-6 py-3 rounded-full font-bold hover:bg-[#C4A137] transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  Falar Agora
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 md:mb-10"
          >
            <h2 className="font-display font-bold text-2xl md:text-4xl text-[#0A1628] mb-3 md:mb-4">
              Nossa Localização
            </h2>
            <p className="text-sm md:text-lg text-neutral-600">
              Sediados em Porto Feliz - SP, atendemos as Regiões Metropolitanas de Sorocaba e Campinas
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl overflow-hidden shadow-2xl"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14832.123456789!2d-47.5244!3d-23.2147!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94c5f5b5b5b5b5b5%3A0x1234567890abcdef!2sPorto%20Feliz%2C%20SP!5e0!3m2!1spt-BR!2sbr!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mapa de localização - Porto Feliz, SP"
            ></iframe>
          </motion.div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default ContatoPage;